import { useState, useEffect } from 'react';
import { api, formatCurrency, getCurrentMonth } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Package, Plus, X, Trash2, ChevronDown, ChevronUp, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

const MOTIFS = ['Qualité insuffisante', 'Erreur de commande', 'Produit abîmé', 'Périmé', 'Non conforme', 'Autre'];

const RetoursMarchandiseSection = ({ source = 'patron' }) => {
  const [retours, setRetours] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [currentMonth] = useState(getCurrentMonth());
  const [form, setForm] = useState({
    fournisseur: '',
    date: new Date().toISOString().split('T')[0],
    motif: '',
    note: '',
    produits: [{ nom: '', quantite: '', prix_unitaire: '', poids: '', unite: 'kg' }],
  });

  const fetchRetours = async () => {
    try {
      const res = await api.get(`/retours?mois=${currentMonth}`);
      setRetours(res.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchRetours(); }, [currentMonth]);

  const addProductLine = () => {
    setForm(f => ({
      ...f,
      produits: [...f.produits, { nom: '', quantite: '', prix_unitaire: '', poids: '', unite: 'kg' }],
    }));
  };

  const updateProduct = (idx, field, value) => {
    setForm(f => {
      const p = [...f.produits];
      p[idx] = { ...p[idx], [field]: value };
      return { ...f, produits: p };
    });
  };

  const removeProduct = (idx) => {
    setForm(f => ({ ...f, produits: f.produits.filter((_, i) => i !== idx) }));
  };

  const calcLineMontant = (p) => {
    const prix = parseFloat(p.prix_unitaire) || 0;
    const poids = parseFloat(p.poids) || 0;
    const qte = parseFloat(p.quantite) || 0;
    if (poids > 0) return round2(prix * poids);
    return round2(prix * qte);
  };

  const round2 = (v) => Math.round(v * 100) / 100;

  const totalForm = form.produits.reduce((s, p) => s + calcLineMontant(p), 0);

  const submitRetour = async () => {
    if (!form.fournisseur) { toast.error('Indiquez le fournisseur'); return; }
    const produits = form.produits.filter(p => p.nom).map(p => ({
      nom: p.nom,
      quantite: parseFloat(p.quantite) || 0,
      prix_unitaire: parseFloat(p.prix_unitaire) || 0,
      poids: parseFloat(p.poids) || 0,
      unite: p.unite || 'kg',
    }));
    if (produits.length === 0) { toast.error('Ajoutez au moins un produit'); return; }

    try {
      const res = await api.post('/retours', {
        fournisseur: form.fournisseur,
        date: form.date || new Date().toISOString().split('T')[0],
        produits,
        motif: form.motif,
        note: form.note,
        source,
      });
      if (res.data.error) { toast.error(res.data.error); return; }
      toast.success(`Retour enregistré — ${formatCurrency(res.data.total_ht)} HT`);
      setForm({
        fournisseur: '', date: new Date().toISOString().split('T')[0], motif: '', note: '',
        produits: [{ nom: '', quantite: '', prix_unitaire: '', poids: '', unite: 'kg' }],
      });
      setShowForm(false);
      fetchRetours();
    } catch { toast.error('Erreur'); }
  };

  const deleteRetour = async (id) => {
    try {
      await api.delete(`/retours/${id}`);
      toast.success('Retour supprimé');
      fetchRetours();
    } catch { toast.error('Erreur'); }
  };

  const totalMois = retours.reduce((s, r) => s + (r.total_ht || 0), 0);

  return (
    <div className="kpi-card border-emerald-400/20" data-testid="retours-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Undo2 size={18} className="text-emerald-400" />
          <h3 className="font-title text-lg">Retours marchandise</h3>
          {retours.length > 0 && (
            <span className="bg-emerald-400/20 text-emerald-400 text-xs font-mono px-2 py-0.5 rounded-full">
              {retours.length} · {formatCurrency(totalMois)}
            </span>
          )}
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          size="sm"
          className={showForm ? 'bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text))]' : 'bg-emerald-400/20 text-emerald-400 hover:bg-emerald-400/30'}
          data-testid="add-retour-btn"
        >
          {showForm ? <X size={14} /> : <><Plus size={14} className="mr-1" /> Nouveau retour</>}
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-lemas-bg/60 rounded-lg p-4 mb-4 space-y-3 animate-slide-up" data-testid="retour-form">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Fournisseur</label>
              <Input
                value={form.fournisseur}
                onChange={e => setForm(f => ({ ...f, fournisseur: e.target.value }))}
                placeholder="ex: Metro, Transgourmet..."
                className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]"
                data-testid="retour-fournisseur"
              />
            </div>
            <div>
              <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Date retour</label>
              <Input
                type="date"
                value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] font-mono"
                data-testid="retour-date"
              />
            </div>
            <div>
              <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Motif</label>
              <select
                value={form.motif}
                onChange={e => setForm(f => ({ ...f, motif: e.target.value }))}
                className="w-full bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] rounded-md p-2 text-sm"
                data-testid="retour-motif"
              >
                <option value="">Choisir...</option>
                {MOTIFS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          <p className="text-xs text-[rgb(var(--lm-text-secondary))] font-medium">Produits retournés</p>
          {form.produits.map((p, idx) => (
            <div key={idx} className="flex items-center gap-2 flex-wrap" data-testid={`retour-produit-${idx}`}>
              <Input
                value={p.nom}
                onChange={e => updateProduct(idx, 'nom', e.target.value)}
                placeholder="Produit"
                className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] flex-1 min-w-[120px]"
                data-testid={`retour-produit-nom-${idx}`}
              />
              <Input
                type="number"
                step="0.01"
                value={p.quantite}
                onChange={e => updateProduct(idx, 'quantite', e.target.value)}
                placeholder="Qté"
                className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] w-16 font-mono text-center"
                data-testid={`retour-produit-qte-${idx}`}
              />
              <Input
                type="number"
                step="0.01"
                value={p.prix_unitaire}
                onChange={e => updateProduct(idx, 'prix_unitaire', e.target.value)}
                placeholder="Prix/kg"
                className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] w-20 font-mono text-center"
                data-testid={`retour-produit-prix-${idx}`}
              />
              <Input
                type="number"
                step="0.01"
                value={p.poids}
                onChange={e => updateProduct(idx, 'poids', e.target.value)}
                placeholder="Poids (kg)"
                className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] w-24 font-mono text-center"
                data-testid={`retour-produit-poids-${idx}`}
              />
              <span className="font-mono text-xs text-emerald-400 min-w-[60px] text-right">
                {formatCurrency(calcLineMontant(p))}
              </span>
              {form.produits.length > 1 && (
                <button onClick={() => removeProduct(idx)} className="text-[rgb(var(--lm-text-muted))] hover:text-red-400">
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
          <button onClick={addProductLine} className="text-xs text-emerald-400 hover:text-emerald-300" data-testid="retour-add-produit">
            + Ajouter un produit
          </button>

          <Input
            value={form.note}
            onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
            placeholder="Note (optionnel)"
            className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-sm"
            data-testid="retour-note"
          />

          <div className="flex items-center justify-between">
            <span className="text-sm text-[rgb(var(--lm-text-secondary))]">
              Total retour : <span className="font-mono text-emerald-400 font-semibold">{formatCurrency(totalForm)} HT</span>
            </span>
            <Button
              onClick={submitRetour}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="submit-retour"
            >
              <Undo2 size={14} className="mr-2" /> Enregistrer le retour
            </Button>
          </div>
        </div>
      )}

      {/* Retours list */}
      {retours.length > 0 ? (
        <div className="space-y-2">
          {retours.map(r => (
            <div key={r.id} className="rounded-lg bg-lemas-bg/40 border border-[rgb(var(--lm-border))] overflow-hidden" data-testid={`retour-${r.id}`}>
              <div
                className="flex items-center justify-between p-3 cursor-pointer"
                onClick={() => setExpanded(expanded === r.id ? null : r.id)}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-400/15 text-emerald-400">Retour</span>
                  <span className="text-sm font-medium truncate">{r.fournisseur}</span>
                  <span className="font-mono text-xs text-[rgb(var(--lm-text-muted))]">{r.date}</span>
                  {r.motif && <span className="text-xs text-[rgb(var(--lm-text-muted))] hidden sm:inline">· {r.motif}</span>}
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <span className="font-mono text-emerald-400 font-medium">{formatCurrency(r.total_ht)}</span>
                  <span className="text-xs text-[rgb(var(--lm-text-muted))]">{r.produits?.length || 0} prod.</span>
                  {expanded === r.id ? <ChevronUp size={14} className="text-[rgb(var(--lm-text-muted))]" /> : <ChevronDown size={14} className="text-[rgb(var(--lm-text-muted))]" />}
                </div>
              </div>
              {expanded === r.id && (
                <div className="px-3 pb-3 space-y-2 animate-slide-up">
                  {r.produits?.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-sm bg-lemas-bg/40 rounded p-2">
                      <div>
                        <span className="font-medium">{p.nom}</span>
                        {p.poids > 0 && <span className="text-xs text-[rgb(var(--lm-text-muted))] ml-2">{p.poids} kg</span>}
                        {p.quantite > 0 && !p.poids && <span className="text-xs text-[rgb(var(--lm-text-muted))] ml-2">x{p.quantite}</span>}
                      </div>
                      <div className="text-right">
                        <span className="font-mono text-xs">{formatCurrency(p.prix_unitaire)}/{p.unite || 'kg'}</span>
                        <span className="font-mono text-xs text-emerald-400 ml-2">{formatCurrency(p.montant || 0)}</span>
                      </div>
                    </div>
                  ))}
                  {r.note && <p className="text-xs text-[rgb(var(--lm-text-secondary))] italic">{r.note}</p>}
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" variant="ghost" onClick={() => deleteRetour(r.id)} className="text-lemas-danger text-xs" data-testid={`delete-retour-${r.id}`}>
                      <Trash2 size={12} className="mr-1" /> Supprimer
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : !showForm ? (
        <p className="text-sm text-[rgb(var(--lm-text-muted))] text-center py-2">Aucun retour ce mois</p>
      ) : null}
    </div>
  );
};

export default RetoursMarchandiseSection;
